import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PublicHeader } from "@/components/PublicHeader";
import { ArrowLeft, Check, X, Receipt, Info } from "lucide-react";

/** Public fee schedule — every charge, in one honest list. */
export default function Fees() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/fees`).then((r) => setData(r.data));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 sm:py-16">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-8" data-testid="fees-back">
          <ArrowLeft className="h-4 w-4 me-1.5" />Back
        </Button>

        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
          <Receipt className="h-3.5 w-3.5" />Fee schedule
        </div>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="fees-title">
          What you pay.<br />
          <span className="text-[color:var(--tharwy-indigo)]">Nothing you don't.</span>
        </h1>
        <p className="mt-5 max-w-2xl text-base text-muted-foreground leading-relaxed">
          One page, every charge. Each fee that applies to an order is itemised on the confirmation
          screen before you press Confirm, so you always see the total before the money moves.
        </p>

        {!data ? (
          <p className="mt-12 text-sm text-muted-foreground">Loading the numbers...</p>
        ) : (
          <>
            <div className="mt-12 space-y-10" data-testid="fees-groups">
              {data.groups.map((g) => (
                <section key={g.title} data-testid={`fees-group-${g.title.replace(/\s+/g, "-").toLowerCase()}`}>
                  <h2 className="text-base md:text-lg font-semibold">{g.title}</h2>
                  <div className="mt-4 divide-y rounded-2xl border">
                    {g.items.map((it) => (
                      <div key={it.name} className="grid gap-1.5 p-4 sm:grid-cols-[1fr_auto] sm:items-start sm:gap-6">
                        <div>
                          <div className="text-sm font-medium">{it.name}</div>
                          {it.detail && (
                            <div className="mt-1 text-xs text-muted-foreground leading-relaxed">{it.detail}</div>
                          )}
                          {it.example && (
                            <div className="mt-1.5 text-xs numeric text-[color:var(--tharwy-indigo)]">{it.example}</div>
                          )}
                        </div>
                        <div className="numeric text-sm font-semibold sm:text-end sm:whitespace-nowrap">{it.fee}</div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <Card className="mt-10 p-6 shadow-premium" data-testid="fees-never">
              <h2 className="text-base md:text-lg font-semibold">What we never charge for</h2>
              <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
                {data.never_charged.map((n) => (
                  <li key={n} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <div className="mt-10 space-y-3" data-testid="fees-notes">
              {data.notes.map((n) => (
                <div key={n} className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed">
                  <Info className="h-3.5 w-3.5 mt-px shrink-0" /><span>{n}</span>
                </div>
              ))}
              <div className="text-xs text-muted-foreground">
                {data.currency_note} Last updated {data.updated}.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
